// Utility functions to handle contact data format conversion

export type ContactArray = Array<{
  phone?: string;
  email?: string; 
  type: "official" | "personal" | "cell" | "store" | "others";
  is_primary: boolean;
}>;

export type PhoneArray = Array<{
  phone: string;
  type: "official" | "personal" | "cell" | "store" | "others";
  is_primary: boolean;
}>;

export type EmailArray = Array<{
  email: string;
  type: "official" | "personal" | "cell" | "store" | "others";
  is_primary: boolean;
}>;

/**
 * Extract primary phone number from phone data (string or array)
 */
export function extractPrimaryPhone(phone?: string | PhoneArray): string {
  if (!phone) return "";
  
  if (typeof phone === "string") return phone;
  
  if (Array.isArray(phone) && phone.length > 0) {
    return phone.find(p => p.is_primary)?.phone || phone[0]?.phone || "";
  }
  
  return "";
}

/**
 * Extract primary email from email data (string or array)
 */
export function extractPrimaryEmail(email?: string | EmailArray): string {
  if (!email) return "";
  
  if (typeof email === "string") return email;
  
  if (Array.isArray(email) && email.length > 0) {
    return email.find(e => e.is_primary)?.email || email[0]?.email || "";
  }
  
  return "";
}

/**
 * Convert string values to contact arrays for form initialization
 */
export function convertToContactArrays(contact: {
  phone?: string | PhoneArray;
  email?: string | EmailArray;
}): {
  phone: PhoneArray;
  email: EmailArray;
} {
  const phoneArray: PhoneArray = [];
  const emailArray: EmailArray = [];
  
  // Handle phone conversion
  if (contact.phone) {
    if (typeof contact.phone === "string" && contact.phone.trim()) {
      phoneArray.push({
        phone: contact.phone.trim(),
        type: "official",
        is_primary: true
      });
    } else if (Array.isArray(contact.phone)) {
      phoneArray.push(...contact.phone.filter(p => p.phone?.trim()));
    }
  }
  
  // Handle email conversion
  if (contact.email) {
    if (typeof contact.email === "string" && contact.email.trim()) {
      emailArray.push({
        email: contact.email.trim(),
        type: "official", 
        is_primary: true
      });
    } else if (Array.isArray(contact.email)) {
      emailArray.push(...contact.email.filter(e => e.email?.trim()));
    }
  }
  
  return { phone: phoneArray, email: emailArray };
}

/**
 * Convert contact arrays to clean format with required properties
 */
export function cleanContactArrays(data: {
  phone?: Array<{
    phone?: string;
    type?: "official" | "personal" | "cell" | "store" | "others";
    is_primary?: boolean;
  }>;
  email?: Array<{
    email?: string;
    type?: "official" | "personal" | "cell" | "store" | "others";
    is_primary?: boolean;
  }>;
}): {
  phone: PhoneArray;
  email: EmailArray;
} {
  const phoneArray: PhoneArray = data.phone?.filter(p => p.phone?.trim() && p.type).map(p => ({
    phone: p.phone!,
    type: p.type!,
    is_primary: p.is_primary || false
  })) || [];
  
  const emailArray: EmailArray = data.email?.filter(e => e.email?.trim() && e.type).map(e => ({
    email: e.email!,
    type: e.type!,
    is_primary: e.is_primary || false
  })) || [];
  
  return { phone: phoneArray, email: emailArray };
}

/**
 * Parse contact data from multiple possible formats for forms
 */
export function parseContactDataForForms(contact: any): {
  phone: PhoneArray;
  email: EmailArray;
} {
  const phoneArray: PhoneArray = [];
  const emailArray: EmailArray = [];
  
  // Priority order: phones/emails arrays → legacy phone/email arrays → fallback strings
  
  // Handle new phones array structure (with 'number' and 'isPrimary' fields)
  if (contact.phones && Array.isArray(contact.phones)) {
    contact.phones.forEach((phoneItem: any) => {
      const phoneNumber = phoneItem.number || phoneItem.phone;
      if (phoneNumber && phoneNumber.trim()) {
        phoneArray.push({
          phone: phoneNumber.trim(),
          type: phoneItem.type || "official",
          is_primary: phoneItem.isPrimary || phoneItem.is_primary || false
        });
      }
    });
  }
  // Handle legacy phone data - can be string, array of objects, or array of strings
  else if (contact.phone) {
    if (typeof contact.phone === "string" && contact.phone.trim()) {
      phoneArray.push({
        phone: contact.phone.trim(),
        type: "official",
        is_primary: true
      });
    } else if (Array.isArray(contact.phone)) {
      contact.phone.forEach((phoneItem: any) => {
        if (typeof phoneItem === "string" && phoneItem.trim()) {
          phoneArray.push({
            phone: phoneItem.trim(),
            type: "official",
            is_primary: false
          });
        } else if (phoneItem.phone && phoneItem.phone.trim()) {
          phoneArray.push({
            phone: phoneItem.phone.trim(),
            type: phoneItem.type || "official",
            is_primary: phoneItem.is_primary || false
          });
        }
      });
    }
  }
  
  // Handle new emails array structure (with 'address' and 'isPrimary' fields)
  if (contact.emails && Array.isArray(contact.emails)) {
    contact.emails.forEach((emailItem: any) => {
      const emailAddress = emailItem.address || emailItem.email;
      if (emailAddress && emailAddress.trim()) {
        emailArray.push({
          email: emailAddress.trim(),
          type: emailItem.type || "official",
          is_primary: emailItem.isPrimary || emailItem.is_primary || false
        });
      }
    });
  }
  // Handle legacy email data - can be string, array of objects, or array of strings
  else if (contact.email) {
    if (typeof contact.email === "string" && contact.email.trim()) {
      emailArray.push({
        email: contact.email.trim(),
        type: "official",
        is_primary: true
      });
    } else if (Array.isArray(contact.email)) {
      contact.email.forEach((emailItem: any) => {
        if (typeof emailItem === "string" && emailItem.trim()) {
          emailArray.push({
            email: emailItem.trim(),
            type: "official",
            is_primary: false
          });
        } else if (emailItem.email && emailItem.email.trim()) {
          emailArray.push({
            email: emailItem.email.trim(),
            type: emailItem.type || "official",
            is_primary: emailItem.is_primary || false
          });
        }
      });
    }
  }
  
  // Fallback to metadata phone if no phones found
  if (phoneArray.length === 0 && contact.metadata?.phone && contact.metadata.phone.trim()) {
    phoneArray.push({
      phone: contact.metadata.phone.trim(),
      type: "official",
      is_primary: true
    });
  }
  
  // Fallback to metadata email if no emails found
  if (emailArray.length === 0 && contact.metadata?.email && contact.metadata.email.trim()) {
    emailArray.push({
      email: contact.metadata.email.trim(),
      type: "official",
      is_primary: true
    });
  }
  
  // Ensure at least one primary per type if any exist
  if (phoneArray.length > 0 && !phoneArray.some(p => p.is_primary)) {
    phoneArray[0].is_primary = true;
  }
  if (emailArray.length > 0 && !emailArray.some(e => e.is_primary)) {
    emailArray[0].is_primary = true;
  }
  
  return { phone: phoneArray, email: emailArray };
}